import React, { useState, useEffect } from 'react';
import EmployeeArrivalQrCode from '@/components/employees/EmployeeArrivalQrCode';
import InProgressShiftDisplay from '@/components/employees/InProgressShiftDisplay';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import EmployeeShiftsTab from '@/components/shifts/EmployeeShiftsTab';
import AvailableShiftsTab from '@/components/shifts/AvailableShiftsTab';
import EmployeeEventsTab from '@/components/events/EmployeeEventsTab';
import { WhatsAppTab } from '@/components/whatsapp';
import EmployeePayrollTab from '@/components/payroll/EmployeePayrollTab';
import { useAuth } from '@/context/AuthContext';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types/index';

// Placeholder for My Time Logs content
const MyTimeLogsSection: React.FC = () => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">My Recent Time Logs</CardTitle>
        <CardDescription>Review your clock-in and clock-out history.</CardDescription>
      </CardHeader>
      <CardContent>
        {/* TODO: Implement time log display logic for the logged-in employee */}
        <p>Your recent time logs will appear here.</p>
        <p className="mt-2">Consider fetching and displaying time_logs for the current employee.</p>
      </CardContent>
    </Card>
  );
};


const EmployeePortalPage: React.FC = () => {
  const { user } = useAuth();
  const { employeePortalTab: activeView, setEmployeePortalTab: setActiveView } = useDashboardTab();
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [loadingEmployee, setLoadingEmployee] = useState(true);
  
  // Get current employee's business information
  useEffect(() => {
    const fetchCurrentEmployee = async () => {
      if (!user?.id) {
        setLoadingEmployee(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('employees')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching employee data:', error);
          setCurrentEmployee(null);
        } else if (data) {
          setCurrentEmployee(data);
        } else {
          setCurrentEmployee(null);
        }
      } catch (error) {
        console.error('Exception during employee lookup:', error);
        setCurrentEmployee(null);
      } finally {
        setLoadingEmployee(false);
      }
    };

    fetchCurrentEmployee();
  }, [user?.id]);

  // Function to render content based on active view
  const renderContent = () => {
    if (!currentEmployee) {
      return null;
    }

    switch (activeView) {
      case 'my-dashboard':
        return (
          <div className="flex flex-col items-center space-y-8">
            <EmployeeArrivalQrCode employeeId={currentEmployee.id} businessId={currentEmployee.business_id} />
            <InProgressShiftDisplay 
              employeeId={currentEmployee.id} 
              businessId={currentEmployee.business_id}
              onNavigateToShifts={() => setActiveView('my-shifts')}
            />
            <MyTimeLogsSection />
          </div>
        );
      case 'my-shifts':
        return <EmployeeShiftsTab employeeId={currentEmployee.id} businessId={currentEmployee.business_id} />;
      case 'available-shifts':
        return <AvailableShiftsTab employeeId={currentEmployee.id} businessId={currentEmployee.business_id} />;
      case 'my-events':
        return <EmployeeEventsTab employeeId={currentEmployee.id} />;
      case 'conversations':
        return <WhatsAppTab businessId={currentEmployee.business_id} />;
      case 'my-payroll':
        return <EmployeePayrollTab employeeId={currentEmployee.id} businessId={currentEmployee.business_id} />;
      default:
        return (
          <div className="flex flex-col items-center space-y-8">
            <EmployeeArrivalQrCode employeeId={currentEmployee.id} businessId={currentEmployee.business_id} />
            <InProgressShiftDisplay 
              employeeId={currentEmployee.id} 
              businessId={currentEmployee.business_id}
              onNavigateToShifts={() => setActiveView('my-shifts')}
            />
            <MyTimeLogsSection />
          </div>
        );
    }
  };
  
  if (loadingEmployee) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <div className="ml-4">Loading employee information...</div>
      </div>
    );
  }

  if (!currentEmployee) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-4">Access Denied</h2>
          <p className="text-muted-foreground">You are not registered as an employee.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <h1 className="text-3xl font-bold mb-8 text-center text-gray-900 dark:text-gray-100">
        Employee Portal - {currentEmployee.name}
      </h1>
      <div className="w-full overflow-x-hidden">
        {renderContent()}
      </div>
    </>
  );
};

export default EmployeePortalPage;
